/**
 * 主軸カテゴリー 作成/編集ダイアログ。v3.1.3
 *
 * カテゴリー単位で書類用ロゴ画像を設定できる（見積書・請求書の公開ビューに反映）。
 * Storage 保存先: system/imgs/* (ImageGalleryDialog のデフォルト)
 */
import type {
  BankAccountType,
  CategoryBankAccount,
  CategoryLogoPosition,
  CompanyInfo,
  ProductCategoryKind,
} from '@goenhub/shared';
import { SUPPORTED_CURRENCIES, currencySymbol } from '@goenhub/shared';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmailChipsInput } from '@/components/common/EmailChipsInput';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import {
  useUpsertProductCategory,
  type ProductCategoryRecord,
} from '@/hooks/useProductCategories';
import { useProductCatalogs } from '@/hooks/useProductCatalogs';
import { ImageFieldEditor } from '@/pages/landings/components/ImageFieldEditor';

const INHERIT_VALUE = '__inherit__';

interface Props {
  open: boolean;
  existing: ProductCategoryRecord | null;
  onOpenChange: (open: boolean) => void;
}

const LOGO_POSITION_OPTIONS: ReadonlyArray<{
  value: CategoryLogoPosition;
  label: string;
}> = [
  { value: 'top-left', label: '左上' },
  { value: 'top-center', label: '中央上' },
  { value: 'top-right', label: '右上 (既定)' },
];

const KIND_OPTIONS: ReadonlyArray<{
  value: ProductCategoryKind;
  label: string;
  hint: string;
}> = [
  { value: 'btob', label: 'BtoB', hint: '法人向け' },
  { value: 'btoc', label: 'BtoC', hint: '個人向け' },
  { value: 'mixed', label: '両方', hint: '法人・個人どちらも' },
];

const ACCOUNT_TYPE_OPTIONS: ReadonlyArray<{
  value: BankAccountType;
  label: string;
}> = [
  { value: 'ordinary', label: '普通' },
  { value: 'checking', label: '当座' },
  { value: 'savings', label: '貯蓄' },
];

const EMPTY_BANK = {
  label: '',
  bankName: '',
  branchName: '',
  accountType: 'ordinary' as BankAccountType,
  accountNumber: '',
  accountHolder: '',
};

const EMPTY_COMPANY = {
  name: '',
  representative: '',
  postalCode: '',
  address: '',
  phone: '',
  email: '',
};

export function ProductCategoryDialog({ open, existing, onOpenChange }: Props) {
  const upsert = useUpsertProductCategory();
  const { data: mailAccounts } = useMailAccounts();
  const { data: catalogs } = useProductCatalogs();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [displayOrder, setDisplayOrder] = useState<string>('');
  const [logoStoragePath, setLogoStoragePath] = useState<string | null>(null);
  const [logoPosition, setLogoPosition] =
    useState<CategoryLogoPosition>('top-right');
  const [kind, setKind] = useState<ProductCategoryKind>('mixed');
  /** null = 営業設定既定にフォールバック / それ以外は mail_accounts ID。 */
  const [mailAccountId, setMailAccountId] = useState<string | null>(null);
  /** v3.2.21: 紐付けブランド名一覧 (旧 catalogIds の置き換え) */
  const [linkedBrandNames, setLinkedBrandNames] = useState<string[]>([]);
  /** v3.2.25: 同意ステップ (成約 → 同意 → 入金) を経由するか。 */
  const [requiresConsent, setRequiresConsent] = useState<boolean>(false);
  /** v3.3.x: メーカー (発注先) メールアドレス（複数, To）。 */
  const [manufacturerEmails, setManufacturerEmails] = useState<string[]>([]);
  /** v3.3.x: メーカー宛メールの既定 CC（複数）。 */
  const [manufacturerCcEmails, setManufacturerCcEmails] = useState<string[]>([]);
  /** v3.3.x: メーカー取引通貨（発注通貨）。既定 EUR。 */
  const [manufacturerCurrency, setManufacturerCurrency] = useState<string>('EUR');
  /** v3.3.x: カテゴリー別の請求書振込先口座。全項目空 = オーナー既定にフォールバック。 */
  const [bank, setBank] = useState<{
    label: string;
    bankName: string;
    branchName: string;
    accountType: BankAccountType;
    accountNumber: string;
    accountHolder: string;
  }>(EMPTY_BANK);
  /** v3.3.x: カテゴリー別の発行元会社情報。全項目空 = デフォルトにフォールバック。 */
  const [company, setCompany] = useState<CompanyInfo>(EMPTY_COMPANY);

  useEffect(() => {
    if (open) {
      setName(existing?.name ?? '');
      setDescription(existing?.description ?? '');
      setDisplayOrder(
        typeof existing?.displayOrder === 'number'
          ? String(existing.displayOrder)
          : '',
      );
      setLogoStoragePath(existing?.logoStoragePath ?? null);
      setLogoPosition(existing?.logoPosition ?? 'top-right');
      setKind(existing?.kind ?? 'mixed');
      setMailAccountId(existing?.mailAccountId ?? null);
      setLinkedBrandNames(existing?.linkedBrandNames ?? []);
      setRequiresConsent(Boolean(existing?.requiresConsent));
      setManufacturerEmails(
        existing?.manufacturerEmails ??
          (existing?.manufacturerEmail ? [existing.manufacturerEmail] : []),
      );
      setManufacturerCcEmails(existing?.manufacturerCcEmails ?? []);
      setManufacturerCurrency(existing?.manufacturerCurrency ?? 'EUR');
      setBank(
        existing?.bankAccount
          ? {
              label: existing.bankAccount.label ?? '',
              bankName: existing.bankAccount.bankName ?? '',
              branchName: existing.bankAccount.branchName ?? '',
              accountType: existing.bankAccount.accountType ?? 'ordinary',
              accountNumber: existing.bankAccount.accountNumber ?? '',
              accountHolder: existing.bankAccount.accountHolder ?? '',
            }
          : EMPTY_BANK,
      );
      setCompany(
        existing?.companyInfo
          ? {
              name: existing.companyInfo.name ?? '',
              representative: existing.companyInfo.representative ?? '',
              postalCode: existing.companyInfo.postalCode ?? '',
              address: existing.companyInfo.address ?? '',
              phone: existing.companyInfo.phone ?? '',
              email: existing.companyInfo.email ?? '',
            }
          : EMPTY_COMPANY,
      );
    }
  }, [open, existing]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('カテゴリー名は必須です');
      return;
    }
    const orderNum = displayOrder.trim()
      ? Number(displayOrder)
      : undefined;
    if (orderNum !== undefined && !Number.isFinite(orderNum)) {
      toast.error('表示順は数値で入力してください');
      return;
    }
    // 振込先: 全項目空ならクリア(null=オーナー既定にフォールバック)、
    // 一部のみ入力なら必須4項目をチェック
    const bankFilled =
      bank.bankName.trim() ||
      bank.branchName.trim() ||
      bank.accountNumber.trim() ||
      bank.accountHolder.trim() ||
      bank.label.trim();
    let bankAccount: CategoryBankAccount | null = null;
    if (bankFilled) {
      if (
        !bank.bankName.trim() ||
        !bank.branchName.trim() ||
        !bank.accountNumber.trim() ||
        !bank.accountHolder.trim()
      ) {
        toast.error('振込先は 銀行名 / 支店名 / 口座番号 / 口座名義 をすべて入力してください');
        return;
      }
      bankAccount = {
        label: bank.label.trim() || null,
        bankName: bank.bankName.trim(),
        branchName: bank.branchName.trim(),
        accountType: bank.accountType,
        accountNumber: bank.accountNumber.trim(),
        accountHolder: bank.accountHolder.trim(),
      };
    }
    // 会社情報: 全項目空ならクリア(null=デフォルトにフォールバック)、一部入力なら会社名必須
    const companyFilled =
      company.name.trim() ||
      company.representative.trim() ||
      company.postalCode.trim() ||
      company.address.trim() ||
      company.phone.trim() ||
      company.email.trim();
    let companyInfo: CompanyInfo | null = null;
    if (companyFilled) {
      if (!company.name.trim()) {
        toast.error('カテゴリー別会社情報を設定する場合は会社名が必須です');
        return;
      }
      companyInfo = {
        name: company.name.trim(),
        representative: company.representative.trim(),
        postalCode: company.postalCode.trim(),
        address: company.address.trim(),
        phone: company.phone.trim(),
        email: company.email.trim(),
      };
    }
    try {
      const r = await upsert.mutateAsync({
        id: existing?.id,
        name: trimmed,
        description: description.trim() || null,
        displayOrder: orderNum,
        isActive: true,
        kind,
        logoStoragePath: logoStoragePath ?? null,
        logoPosition: logoStoragePath ? logoPosition : null,
        mailAccountId: mailAccountId,
        linkedBrandNames,
        requiresConsent,
        manufacturerEmails: manufacturerEmails.length > 0 ? manufacturerEmails : null,
        manufacturerCcEmails:
          manufacturerCcEmails.length > 0 ? manufacturerCcEmails : null,
        manufacturerCurrency: manufacturerCurrency || 'EUR',
        bankAccount,
        companyInfo,
        // 旧データの catalogIds は新規には書き込まない (linkedBrandNames が優先)
      });
      const renamedMsg =
        r.renamedDeals + r.renamedForms > 0
          ? ` (商談 ${r.renamedDeals} 件 / フォーム ${r.renamedForms} 件 の表示名を更新)`
          : '';
      toast.success(
        `${existing ? '更新' : '作成'}しました${renamedMsg}`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(
        `失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {existing ? 'カテゴリーを編集' : 'カテゴリーを追加'}
          </DialogTitle>
          <DialogDescription>
            主軸カテゴリーは商談・フォームに紐付くマスタです。
            名称を変更すると、既存の参照先で自動的に表示名が更新されます。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="cat-name">カテゴリー名</Label>
            <Input
              id="cat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: プロ機器"
            />
          </div>
          <div>
            <Label htmlFor="cat-desc">説明 (任意)</Label>
            <Textarea
              id="cat-desc"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="例: 業務用映像機材のラインナップ"
            />
          </div>
          <div>
            <Label className="text-xs">顧客タイプ</Label>
            <div className="flex gap-2 mt-1">
              {KIND_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex-1 cursor-pointer text-center text-xs py-2 border rounded-sm transition-colors ${
                    kind === opt.value
                      ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                      : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                  }`}
                  title={opt.hint}
                >
                  <input
                    type="radio"
                    name="cat-kind"
                    value={opt.value}
                    checked={kind === opt.value}
                    onChange={() => setKind(opt.value)}
                    className="sr-only"
                  />
                  <div className="font-medium">{opt.label}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">
                    {opt.hint}
                  </div>
                </label>
              ))}
            </div>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              フォーム送信時の Deal 自動振分や、商談ボードのフィルタ補助に使用します。
            </p>
          </div>

          <div>
            <Label htmlFor="cat-order">表示順 (任意)</Label>
            <Input
              id="cat-order"
              type="number"
              inputMode="numeric"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              placeholder="例: 10"
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              昇順で表示されます。空欄の場合は末尾に並びます。
            </p>
          </div>

          <div className="border-t border-[var(--border)] pt-3">
            <ImageFieldEditor
              label="書類用ロゴ画像 (見積・請求の公開ビューに表示)"
              value={logoStoragePath}
              onChange={setLogoStoragePath}
              storagePath="system/imgs"
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              このカテゴリーに紐づく商談から発行する各種書類に共通表示されます。
            </p>
          </div>

          {logoStoragePath && (
            <div>
              <Label className="text-xs">ロゴ表示位置</Label>
              <div className="flex gap-2 mt-1">
                {LOGO_POSITION_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex-1 cursor-pointer text-center text-xs py-2 border rounded-sm transition-colors ${
                      logoPosition === opt.value
                        ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                        : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="logo-position"
                      value={opt.value}
                      checked={logoPosition === opt.value}
                      onChange={() => setLogoPosition(opt.value)}
                      className="sr-only"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* v3.2.21: 紐付けブランド選択 (旧 紐付け商品カタログから移行) */}
          <div className="border-t border-[var(--border)] pt-3">
            <Label>紐付けブランド</Label>
            <p className="text-xs text-[var(--text-muted)] mb-2">
              このカテゴリーで扱う商品ブランド（メーカー）を選択します。
              各ブランドの「適用中」カタログから商品が自動的に引き当てられます。
              バージョン切替時の手動更新は不要。
            </p>
            {(() => {
              // catalogs から unique なブランド名を抽出 (適用中の有無も把握)
              const brandMap = new Map<
                string,
                { hasActive: boolean; versions: number }
              >();
              for (const c of catalogs ?? []) {
                const cur = brandMap.get(c.category) ?? {
                  hasActive: false,
                  versions: 0,
                };
                cur.versions += 1;
                if (c.isActive) cur.hasActive = true;
                brandMap.set(c.category, cur);
              }
              const brands = Array.from(brandMap.entries()).sort(([a], [b]) =>
                a.localeCompare(b, 'ja'),
              );
              if (brands.length === 0) {
                return (
                  <p className="text-xs text-[var(--text-muted)] italic">
                    商品カタログが未登録です。先にマスタ管理 &gt; 商品マスターで作成してください。
                  </p>
                );
              }
              return (
                <div className="space-y-1.5 max-h-48 overflow-y-auto rounded-sm border border-[var(--border)] p-2 bg-[var(--bg-input)]">
                  {brands.map(([brand, info]) => {
                    const checked = linkedBrandNames.includes(brand);
                    return (
                      <label
                        key={brand}
                        className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[var(--bg-hover)] cursor-pointer text-xs"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setLinkedBrandNames((prev) =>
                              checked
                                ? prev.filter((b) => b !== brand)
                                : [...prev, brand],
                            );
                          }}
                        />
                        <span className="font-medium">{brand}</span>
                        <span className="text-[10px] text-[var(--text-muted)]">
                          {info.versions} バージョン
                        </span>
                        {!info.hasActive && (
                          <span className="text-[10px] px-1 py-0.5 rounded bg-[var(--accent-amber-subtle)] text-[var(--accent-amber)]">
                            適用中なし
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              );
            })()}
            <p className="text-[10px] text-[var(--text-muted)] mt-1">
              ※ ブランド = 商品マスターの上位グループ（メーカー名）。
              商品カタログは「ブランド × バージョン」で管理されており、
              各ブランドにつき同時に 1 つの版だけが「適用中」になります。
            </p>
          </div>

          <div className="border-t border-[var(--border)] pt-3">
            <Label>自動メール送信元（任意）</Label>
            <Select
              value={mailAccountId ?? INHERIT_VALUE}
              onValueChange={(v) => setMailAccountId(v === INHERIT_VALUE ? null : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={INHERIT_VALUE}>営業設定の既定にフォールバック</SelectItem>
                {(mailAccounts ?? [])
                  .filter((a) => a.status === 'active')
                  .map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.displayName} &lt;{a.email}&gt;{a.isDefault ? '（既定）' : ''}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              このカテゴリの商談から自動送信されるメール（見積/請求/発注/業務メール）の送信元を指定します。
              未指定時は <code>営業設定 → 自動メール送信</code> の既定アカウントが使用されます。
            </p>
          </div>

          {/* v3.3.x: メーカー (発注先) メールアドレス（複数）+ CC */}
          <div className="border-t border-[var(--border)] pt-3">
            <Label>メーカー (発注先) メールアドレス</Label>
            <EmailChipsInput
              value={manufacturerEmails}
              onChange={setManufacturerEmails}
              placeholder="例: orders@manufacturer.example.com（Enter / カンマで複数追加）"
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              TimeWaver 等の仕入フローで「メーカー見積依頼」「メーカー正式発注」の自動送信先になります。
              複数指定可（全宛先に送信）。空欄のとき該当ボタンは無効化されます。
            </p>
          </div>

          {/* v3.3.x: メーカー宛メールの CC */}
          <div className="border-t border-[var(--border)] pt-3">
            <Label>CC（メーカー宛メール）</Label>
            <EmailChipsInput
              value={manufacturerCcEmails}
              onChange={setManufacturerCcEmails}
              placeholder="CC を追加（Enter / カンマ区切り）"
            />
            <p className="text-xs text-[var(--text-muted)] mt-1">
              メーカー見積依頼・正式発注メールの送信時に、ここのアドレスが自動で CC に入ります。
            </p>
          </div>

          {/* v3.3.x: メーカー取引通貨（発注通貨） */}
          <div className="border-t border-[var(--border)] pt-3">
            <Label htmlFor="cat-mfr-currency">メーカー取引通貨（発注通貨）</Label>
            <select
              id="cat-mfr-currency"
              value={manufacturerCurrency}
              onChange={(e) => setManufacturerCurrency(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm bg-[var(--bg-input)] border border-[var(--border)] rounded-sm"
            >
              {SUPPORTED_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}（{currencySymbol(c).trim()}）
                </option>
              ))}
            </select>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              このメーカー（ブランド）に発注する通貨。発注カード・送金依頼メール・経理の送金依頼リスト・送金完了メールの金額表示に使われます（例: TimeWaver = EUR）。
            </p>
          </div>

          {/* v3.3.x: カテゴリー別 発行元会社情報 */}
          <div className="border-t border-[var(--border)] pt-3">
            <Label>発行元会社情報（任意）</Label>
            <p className="text-xs text-[var(--text-muted)] mb-2">
              このカテゴリーの請求書・見積書・関連メールで使う発行元情報を上書きします。
              空欄のときは <code>システム &gt; 一般設定</code> のデフォルト会社情報を使用します。
            </p>
            <div className="space-y-2">
              <div>
                <Label htmlFor="cat-co-name" className="text-xs">
                  会社名
                </Label>
                <Input
                  id="cat-co-name"
                  value={company.name}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, name: e.target.value }))
                  }
                  placeholder="例: 株式会社カウデザイン"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label htmlFor="cat-co-rep" className="text-xs">
                    代表者名
                  </Label>
                  <Input
                    id="cat-co-rep"
                    value={company.representative}
                    onChange={(e) =>
                      setCompany((c) => ({ ...c, representative: e.target.value }))
                    }
                  />
                </div>
                <div className="w-32">
                  <Label htmlFor="cat-co-zip" className="text-xs">
                    郵便番号
                  </Label>
                  <Input
                    id="cat-co-zip"
                    value={company.postalCode}
                    onChange={(e) =>
                      setCompany((c) => ({ ...c, postalCode: e.target.value }))
                    }
                    placeholder="107-0062"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cat-co-addr" className="text-xs">
                  住所
                </Label>
                <Input
                  id="cat-co-addr"
                  value={company.address}
                  onChange={(e) =>
                    setCompany((c) => ({ ...c, address: e.target.value }))
                  }
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label htmlFor="cat-co-phone" className="text-xs">
                    電話番号
                  </Label>
                  <Input
                    id="cat-co-phone"
                    type="tel"
                    value={company.phone}
                    onChange={(e) =>
                      setCompany((c) => ({ ...c, phone: e.target.value }))
                    }
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="cat-co-email" className="text-xs">
                    メールアドレス
                  </Label>
                  <Input
                    id="cat-co-email"
                    type="email"
                    value={company.email}
                    onChange={(e) =>
                      setCompany((c) => ({ ...c, email: e.target.value }))
                    }
                  />
                </div>
              </div>
              {(company.name ||
                company.representative ||
                company.postalCode ||
                company.address ||
                company.phone ||
                company.email) && (
                <button
                  type="button"
                  className="text-xs text-[var(--accent-red)] hover:underline"
                  onClick={() => setCompany(EMPTY_COMPANY)}
                >
                  会社情報をクリア（デフォルトにフォールバック）
                </button>
              )}
            </div>
          </div>

          {/* v3.3.x: カテゴリー別 振込先口座 */}
          <div className="border-t border-[var(--border)] pt-3">
            <Label>振込先口座（任意）</Label>
            <p className="text-xs text-[var(--text-muted)] mb-2">
              このカテゴリーの商談から発行する請求書の「お振込先」に表示されます。
              空欄のときはオーナーの既定口座にフォールバックします。
              （発行済み請求書は発行時点の口座で固定されるため、ここを変えても過去分は変わりません）
            </p>
            <div className="space-y-2">
              <div>
                <Label htmlFor="bank-name" className="text-xs">
                  銀行名
                </Label>
                <Input
                  id="bank-name"
                  value={bank.bankName}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, bankName: e.target.value }))
                  }
                  placeholder="例: 三菱UFJ銀行"
                />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label htmlFor="bank-branch" className="text-xs">
                    支店名
                  </Label>
                  <Input
                    id="bank-branch"
                    value={bank.branchName}
                    onChange={(e) =>
                      setBank((b) => ({ ...b, branchName: e.target.value }))
                    }
                    placeholder="例: 渋谷支店"
                  />
                </div>
                <div className="w-28">
                  <Label className="text-xs">口座種別</Label>
                  <Select
                    value={bank.accountType}
                    onValueChange={(v) =>
                      setBank((b) => ({
                        ...b,
                        accountType: v as BankAccountType,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACCOUNT_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="bank-number" className="text-xs">
                  口座番号
                </Label>
                <Input
                  id="bank-number"
                  value={bank.accountNumber}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, accountNumber: e.target.value }))
                  }
                  placeholder="例: 1234567"
                />
              </div>
              <div>
                <Label htmlFor="bank-holder" className="text-xs">
                  口座名義（カナ）
                </Label>
                <Input
                  id="bank-holder"
                  value={bank.accountHolder}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, accountHolder: e.target.value }))
                  }
                  placeholder="例: カ）カウデザイン"
                />
              </div>
              <div>
                <Label htmlFor="bank-label" className="text-xs">
                  口座ラベル（任意）
                </Label>
                <Input
                  id="bank-label"
                  value={bank.label}
                  onChange={(e) =>
                    setBank((b) => ({ ...b, label: e.target.value }))
                  }
                  placeholder="例: メイン口座"
                />
              </div>
              {(bank.bankName ||
                bank.branchName ||
                bank.accountNumber ||
                bank.accountHolder ||
                bank.label) && (
                <button
                  type="button"
                  className="text-xs text-[var(--accent-red)] hover:underline"
                  onClick={() => setBank(EMPTY_BANK)}
                >
                  振込先をクリア（オーナー既定にフォールバック）
                </button>
              )}
            </div>
          </div>

          {/* v3.2.25: 同意ステップ ON/OFF */}
          <div className="border-t border-[var(--border)] pt-3">
            <Label>同意ステップ</Label>
            <label className="mt-2 flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresConsent}
                onChange={(e) => setRequiresConsent(e.target.checked)}
                className="mt-0.5"
              />
              <span className="text-xs leading-relaxed">
                成約 → <strong>同意</strong> → 入金 のフローを経由させる
                <br />
                <span className="text-[var(--text-muted)]">
                  OFF のとき: 成約 → 入金 直行（同意タブ・同意確認ラベルは非表示）
                </span>
              </span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={upsert.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={upsert.isPending}
          >
            {upsert.isPending ? '保存中…' : existing ? '更新' : '追加'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
